import { Intersection, Mesh, Object3D, Raycaster, Vector3 } from 'three';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

import { ScanStore } from '../../../../stores/geodata-store-factory/scan-store/scan-store';
import { Cache } from '../../../cache/cache-manager';
import { SkyviewCamera } from '../../../cameras/types';
import { GLTFClearModel } from '../../../loader/gltft/gltf-clear-model';
import { ProjectGeodataManager } from '../../../project-geodata-manager';
import state from '../../../state';
import { closestToRay } from '../../../utilities/pick-near-cursor';
import { ChunkData } from '../../../workers/fetch-chunks';
import { getWebWorkerManager } from '../../../workers/web-worker-manager';
import { Tiled3dModel } from '../type';

export class GltfTree extends Tiled3dModel {
  private destroyed = false;

  private clearModel?: GLTFClearModel;
  private concurrentSize: number;

  private cache: Cache;
  private store?: ScanStore;

  getType(): '3d-tiles-renderer' | 'skymap-tiles-renderer' | 'gltf-tiles-renderer' {
    return 'gltf-tiles-renderer';
  }

  constructor() {
    super();
    // This is the concurrent mesh download
    // This is to limit the number of scan chunks being downloaded per webworker
    // Large number has least overhead, smaller has better response time per download
    this.concurrentSize = 12;

    this.cache = new Cache(state.getValue('scanCacheTime') as number);
  }

  async initialize() {
    if (
      ProjectGeodataManager.instance().isLoading() ||
      !ProjectGeodataManager.instance().hasScanData()
    ) {
      return Promise.resolve();
    }

    getWebWorkerManager().init('gltftChunk');

    const scanGeodata = ProjectGeodataManager.instance().getActiveScanData();
    if (!scanGeodata || !scanGeodata.scanChunkPath) {
      throw new Error('There is not scanChunkPath is available');
    }
    if (!scanGeodata || !scanGeodata.scanClearModel) {
      throw new Error('There is not scanClearModel is available');
    }

    this.store = ProjectGeodataManager.instance().getActiveScanData()?.store;

    // loads the clear model used as a backdrop for the model
    this.clearModel = new GLTFClearModel(this.store);
    await this.clearModel.load(scanGeodata.scanChunkPath, scanGeodata.scanClearModel);
  }

  async activate(): Promise<void> {
    this.destroyed = false;
    return Promise.resolve();
  }

  getClearModel() {
    return this.clearModel;
  }

  getOrigo() {
    return ProjectGeodataManager.instance().getActiveScanData()?.origo ?? new Vector3();
  }

  async loadLodChunks(camera: SkyviewCamera) {
    if (!ProjectGeodataManager.instance().hasScanData()) {
      return;
    }

    const chunks = this.filterChunks(camera);

    const gltfToBeLoader = chunks.reduce((prev: string[], chunk): string[] => {
      const geometry = chunk.data.geometry as string;
      if (!geometry || prev.length >= this.concurrentSize) {
        return prev;
      }

      if (!this.cache.isQueued(geometry) && !this.cache.isCached(geometry)) {
        this.cache.queue(geometry);
        prev.push(geometry);
      } else if (this.cache.isCached(geometry)) {
        this.cache.reset(geometry);
      }
      return prev;
    }, []);

    await this.loadGltf(gltfToBeLoader);
  }

  cullLodChunks(camera: SkyviewCamera) {
    if (!ProjectGeodataManager.instance().hasScanData()) {
      return;
    }

    this.cache.tick();

    const chunks = this.filterChunks(camera);

    const renderChunks = chunks.reduce((prev: Object3D[], chunk): Object3D[] => {
      const geometry = chunk.data.geometry as string;
      if (this.cache.isCached(geometry)) {
        prev.push(this.cache.get(geometry) as Object3D);
      }
      return prev;
    }, []);

    this.remove(...this.children);

    if (this.clearModel) {
      this.add(this.clearModel);
    }
    if (renderChunks.length > 0) {
      this.add(...renderChunks);
    }
  }
  isLoading() {
    return getWebWorkerManager().isLoading();
  }

  /**
   * Picks the hit closest to the ray, for rays that aren't from the camera. Rays from the camera
   * pick with pickNearCursor.
   */
  pick(raycaster: Raycaster) {
    const closest = closestToRay(this.intersect(raycaster));
    return closest ? [closest] : [];
  }

  intersect(raycaster: Raycaster): Intersection[] {
    if (!raycaster) {
      return [];
    }

    // The octree and the chunks that aren't drawn are in the model's own coordinates, around its
    // origo, which is at its position in the scene
    this.updateWorldMatrix(true, false);
    const ownRaycaster = new Raycaster(
      raycaster.ray.origin.clone().sub(this.position),
      raycaster.ray.direction.clone(),
      raycaster.near,
      raycaster.far,
    );
    ownRaycaster.params = raycaster.params;

    const intersections: Intersection[] = [];
    for (const boundingLeaf of this.pickBoundingTree(ownRaycaster)) {
      const chunk = boundingLeaf.object;
      if (chunk.parent === this) {
        chunk.updateWorldMatrix(false, true);
        raycaster.intersectObject(chunk, true, intersections);
      } else {
        // As drawn, without the glTF scene it was loaded in, which may still be its parent
        chunk.updateMatrix();
        chunk.matrixWorld.copy(chunk.matrix);
        for (const child of chunk.children) {
          child.updateMatrixWorld(true);
        }
        for (const intersection of ownRaycaster.intersectObject(chunk, true)) {
          intersection.point.add(this.position);
          intersections.push(intersection);
        }
      }
    }
    return intersections.sort((a, b) => a.distance - b.distance);
  }

  /** The bounds in the scene, at the model's position. */
  getBoundingBox() {
    const scanGeodata = ProjectGeodataManager.instance().getActiveScanData();
    if (!scanGeodata || !scanGeodata.octtree) {
      throw new Error('Scan octtree is missing');
    }

    return scanGeodata.boundingBox.clone().translate(this.position);
  }

  clearCustom() {
    this.cache.clearCache();
    getWebWorkerManager().stopChunks('gltftChunk');
    getWebWorkerManager().init('gltftChunk');
    this.destroyed = true;
  }

  private async loadGltf(parts: string[]) {
    const gltfUrls = await Promise.all(
      parts.map(async (part) => {
        return { part, s3URL: await this.store?.fetchGltfUrl(part) };
      }),
    );

    const loadedGLTFChunks = await getWebWorkerManager().spawnChunkLoader(gltfUrls);

    if (this.destroyed) {
      gltfUrls.map((url) => this.cache.clearCacheElement(url.part));
      return;
    }

    const gltfLoader = new GLTFLoader();
    loadedGLTFChunks.map((chunk: ChunkData) => {
      if (!chunk.data) {
        this.cache.addScanChunk(chunk.id, new Object3D());
      } else {
        gltfLoader.parse(chunk.data, '', (gltf: GLTF) => {
          if (gltf) {
            gltf.scene.rotateX(Math.PI / 2);
            gltf.scene.updateMatrix();

            const node = gltf.scene.children[0].children[0];
            for (const child of node.children) {
              (child as Mesh).geometry?.computeVertexNormals();
              (child as Mesh).geometry?.normalizeNormals();
            }
            this.cache.addScanChunk(
              chunk.id,
              gltf.scene.children[0].children[0],
              undefined,
              chunk.bytes,
            );
          }
        });
      }
    });
  }

  /**
   * The octree's chunks in the camera's view, at their level of detail. The octree is in the
   * model's own coordinates, so the camera is moved there from the model's position in the scene.
   */
  private filterChunks(camera: SkyviewCamera) {
    const scanGeodata = ProjectGeodataManager.instance().getActiveScanData();
    if (!scanGeodata || !scanGeodata.octtree) {
      throw new Error('Scan octtree is missing');
    }

    const octTree = scanGeodata.octtree;
    const treeDepth = octTree.getDepth();

    const octTreeSize = octTree.getSize();
    const largestSize = Math.max(octTreeSize.x, Math.max(octTreeSize.y, octTreeSize.z));

    const LODDepths = [largestSize];
    for (let i = 0; i < treeDepth - 1; i++) {
      LODDepths.push(LODDepths[i] / 2);
    }

    const toOwn = this.position.clone().negate();
    const frustum = camera.getFrustum().clone();
    for (const plane of frustum.planes) {
      plane.translate(toOwn);
    }
    const maxNodeSize = state.getValue('maxNodeSize') / state.getValue('nodeSizeFactor');
    return octTree.filter(
      frustum,
      LODDepths,
      camera.getTHREECamera().getWorldPosition(new Vector3()).add(toOwn),
      -1,
      maxNodeSize,
    );
  }

  private pickBoundingTree(raycaster: Raycaster) {
    const scanGeodata = ProjectGeodataManager.instance().getActiveScanData();
    const octTree = scanGeodata && scanGeodata.octtree;
    if (!octTree) {
      return [];
    }

    const intersectedNodes = octTree.pickTree(raycaster);

    const cachedPointclouChunks = intersectedNodes.reduce(
      (prev: { object: Object3D; distance: number }[], intersectedNode) => {
        if (this.cache.isCached(intersectedNode.node.data.geometry as string)) {
          prev.push({
            object: this.cache.get(intersectedNode.node.data.geometry as string),
            distance: intersectedNode.distance,
          });
        }
        return prev;
      },
      [],
    );

    cachedPointclouChunks.sort((a, b) => {
      return a.distance - b.distance;
    });
    return cachedPointclouChunks;
  }
}
